import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axios';
import PaymentButton from '../../components/PaymentButton';

const UserDashboard = () => {
  const [businesses, setBusinesses] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      // Use the user-scoped API endpoints
      const [businessRes, profileRes] = await Promise.all([
        api.get('/user/my_businesses'),
        api.get('/user/my_profiles')
      ]);
      setBusinesses(businessRes.data);
      setProfiles(profileRes.data);
    } catch (err) {
      console.error("Failed to fetch dashboard data", err);
      setError('Could not load your data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteBusiness = async (id) => {
    if (window.confirm('Are you sure you want to delete this business?')) {
      // Corrected API path
      await api.delete(`/user/business/delete/${id}`); 
      fetchData();
    }
  };

  const handleDeleteProfile = async (id) => {
    if (window.confirm('Are you sure you want to delete this profile?')) {
      // The profile delete path will need to be added to user.py
      // For now, let's assume it will be:
      await api.delete(`/user/profile/delete/${id}`); 
      fetchData();
    }
  };

  if (loading) return <p className="text-center p-8">Loading your dashboard...</p>;
  if (error) return <p className="text-center text-red-500 p-8">{error}</p>;

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Businesses Section */}
      <div className="mb-12">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-3xl font-bold text-gray-800">My Businesses</h1>
          <Link to="/business/new" className="inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
            Add New Business
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {businesses.length === 0 ? (
            <p className="text-gray-500 col-span-full">You haven't added any businesses yet.</p>
          ) : (
            businesses.map(b => (
              <div key={b.id} className="bg-white shadow-md rounded-lg p-6 flex flex-col justify-between">
                <div>
                  <h2 className="text-xl font-semibold mb-2">{b.name}</h2>
                  <p className="text-gray-600 truncate">
                    <a href={`/site/${b.subdomain}`} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{b.subdomain}.saffronpages.com</a>
                  </p>
                  <div className="mt-4">
                    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${b.subscription_status === 'active' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                      {b.subscription_status}
                    </span>
                    {b.subscription_status === 'active' && b.subscription_expires_at && <p className="text-xs text-gray-500 mt-1">Expires: {new Date(b.subscription_expires_at).toLocaleDateString()}</p>}
                  </div>
                </div>
                <div className="mt-6 flex justify-between items-center">
                  <div>
                    <Link to={`/business/edit/${b.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4 font-medium">Edit</Link>
                    <button onClick={() => handleDeleteBusiness(b.id)} className="text-red-600 hover:text-red-900 font-medium">Delete</button>
                  </div>
                  {b.subscription_status !== 'active' && <PaymentButton business={b} onPaymentSuccess={fetchData} />}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Profiles Section */}
      <div>
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-3xl font-bold text-gray-800">My Profiles</h1>
          <Link to="/profile/new" className="inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700">
            Add New Profile
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {profiles.length === 0 ? (
            <p className="text-gray-500 col-span-full">You haven't added any profiles yet.</p>
          ) : (
            profiles.map(p => (
              <div key={p.id} className="bg-white shadow-md rounded-lg p-6 flex flex-col justify-between">
                <div>
                  <h2 className="text-xl font-semibold mb-2">{p.name}</h2>
                  <p className="text-gray-600 truncate">URL Slug: {p.slug}</p>
                </div>
                <div className="mt-6 flex justify-start items-center">
                  <Link to={`/profile/edit/${p.id}`} className="text-indigo-600 hover:text-indigo-900 mr-4 font-medium">Edit</Link>
                  <button onClick={() => handleDeleteProfile(p.id)} className="text-red-600 hover:text-red-900 font-medium">Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;